import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { FiLoader, FiSearch, FiX } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Logo from '../common/Logo';
import {
  getStudentCqpmAnalytics,
  StudentCqpmAnalyticsResponse,
  GradeAnalytics,
  TrackAnalytics
} from '../../api/admin';
import { searchStudentsApi, StudentSearchResult } from '../../config/api';
import { CQPM_TARGETS_BY_TRACK, ACCURACY_PASS_THRESHOLD } from '../../types/constants';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Access denied component
const AccessDenied = () => (
  <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center p-6">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
      <p className="text-base text-gray-600 mt-2">You are not authorized to view this page.</p>
    </div>
  </div>
);

// Header component
const DashboardHeader = () => (
  <h1 className="text-3xl font-bold text-center pt-4 mb-4 flex items-center justify-center gap-3">
    <Logo size={28} />
    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
      Student CQPM Analytics
    </span>
  </h1>
);

interface SearchWithAutocompleteProps {
  onSelectStudent: (email: string, name: string) => void;
  selectedStudentName: string | null;
  selectedStudentEmail: string | null;
  onClearSelection: () => void;
  isLoading?: boolean;
}

const SearchWithAutocomplete: React.FC<SearchWithAutocompleteProps> = ({
  onSelectStudent,
  selectedStudentName,
  selectedStudentEmail,
  onClearSelection,
  isLoading = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<StudentSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const latestSearchQueryRef = useRef('');

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    const normalizedQuery = value.trim();
    latestSearchQueryRef.current = normalizedQuery;

    if (normalizedQuery.length < 2) {
      setSearchResults([]);
      setIsDropdownOpen(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setIsDropdownOpen(true);

    debounceRef.current = setTimeout(async () => {
      try {
        const result = await searchStudentsApi(normalizedQuery);
        if (latestSearchQueryRef.current !== normalizedQuery) {
          return;
        }
        setSearchResults(result.users);
      } catch {
        if (latestSearchQueryRef.current !== normalizedQuery) {
          return;
        }
        setSearchResults([]);
      } finally {
        if (latestSearchQueryRef.current === normalizedQuery) {
          setIsSearching(false);
        }
      }
    }, 300);
  }, []);

  const handleSelectStudent = (student: StudentSearchResult) => {
    const name = `${student.givenName} ${student.familyName}`;
    onSelectStudent(student.email, name);
    setSearchQuery('');
    setIsDropdownOpen(false);
    setSearchResults([]);
  };

  return (
    <Card className="shadow-lg border border-gray-100 mb-6">
      <CardContent className="p-4">
        <div className="relative" ref={dropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search Student by Name or Email
          </label>

          {selectedStudentName ? (
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex-1">
                <p className="font-semibold text-gray-900">{selectedStudentName}</p>
                <p className="text-sm text-gray-600">{selectedStudentEmail}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearSelection}
                className="text-gray-500 hover:text-gray-700"
                disabled={isLoading}
              >
                <FiX className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setIsDropdownOpen(true)}
                  placeholder="Type a name or email to search..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                  disabled={isLoading}
                />
                {(isSearching || isLoading) && (
                  <FiLoader className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 animate-spin" />
                )}
              </div>

              {isDropdownOpen && !isSearching && searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {searchResults.map((student) => (
                    <button
                      key={student.sourcedId}
                      onClick={() => handleSelectStudent(student)}
                      className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                      disabled={isLoading}
                    >
                      <p className="font-medium text-gray-900">
                        {student.givenName} {student.familyName}
                      </p>
                      <p className="text-sm text-gray-500">{student.email}</p>
                    </button>
                  ))}
                </div>
              )}

              {isDropdownOpen && !isSearching && searchQuery.length >= 2 && searchResults.length === 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                  No students found matching "{searchQuery}"
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Track chart component
interface TrackChartProps {
  track: TrackAnalytics;
}

const TrackChart: React.FC<TrackChartProps> = ({ track }) => {
  // Prepare chart data
  const dates = track.assessments.map(a => new Date(a.date).toLocaleDateString());
  const cqpmScores = track.assessments.map(a => a.cqpm);
  const accuracyScores = track.assessments.map(a => a.accuracy);

  // Color points based on accuracy pass/fail (90% threshold)
  const pointColors = accuracyScores.map(accuracy =>
    accuracy >= ACCURACY_PASS_THRESHOLD ? 'rgb(59, 130, 246)' : 'rgb(239, 68, 68)'
  );
  const pointBorderColors = accuracyScores.map(accuracy =>
    accuracy >= ACCURACY_PASS_THRESHOLD ? 'rgb(59, 130, 246)' : 'rgb(239, 68, 68)'
  );

  // Trendline should only have 2 points: start and end
  // Use minCQPM and maxCQPM from the backend calculation
  const trendlineData = track.assessments.map((_, index) => {
    if (index === 0) return track.trendline.minCQPM;
    if (index === track.assessments.length - 1) return track.trendline.maxCQPM;
    return null; // No points in between
  });

  // Get the goal CQPM for this track
  const goalCqpm = CQPM_TARGETS_BY_TRACK[track.trackId as keyof typeof CQPM_TARGETS_BY_TRACK] || 30;
  const goalLineData = track.assessments.map(() => goalCqpm);

  const chartData = {
    labels: dates,
    datasets: [
      {
        label: 'Assessment CQPM',
        data: cqpmScores,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: pointColors,
        pointBackgroundColor: pointColors,
        pointBorderColor: pointBorderColors,
        tension: 0.1,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
      {
        label: 'Trendline',
        data: trendlineData,
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        borderDash: [5, 5],
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
        spanGaps: true, // Connect the first and last point, skipping nulls
      },
      {
        label: 'Goal',
        data: goalLineData,
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        borderDash: [5, 5],
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: track.trackName,
        font: {
          size: 16,
          weight: 'bold',
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            if (context.dataset.label === 'Assessment CQPM') {
              const accuracy = accuracyScores[context.dataIndex];
              return [
                `CQPM: ${Math.round(context.parsed.y)}`,
                `Accuracy: ${Math.round(accuracy)}%`
              ];
            }
            return `${context.dataset.label}: ${Math.round(context.parsed.y)} CQPM`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'CQPM Score',
        },
      },
      x: {
        title: {
          display: true,
          text: 'Assessment Date',
        },
      },
    },
  };

  return (
    <div className="mb-6">
      <div style={{ height: '300px' }}>
        <Line data={chartData} options={options} />
      </div>
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Days Elapsed</p>
            <p className="font-semibold text-gray-900">{track.daysElapsed} days</p>
          </div>
          <div>
            <p className="text-gray-600">XP Spent</p>
            <p className="font-semibold text-gray-900">{track.xpSpent.toFixed(1)} mins</p>
          </div>
          <div>
            <p className="text-gray-600">Total Facts</p>
            <p className="font-semibold text-gray-900">{track.newFactsCount} facts</p>
          </div>
          <div>
            <p className="text-gray-600">Learning Rate</p>
            <p className="font-semibold text-gray-900">{track.learningRate.toFixed(3)} mins/fact/cqpm</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-4 pt-4 border-t border-gray-200">
          <div>
            <p className="text-gray-600">Total Time</p>
            <p className="font-semibold text-gray-900">{track.totalTimeSpent.toFixed(1)} mins</p>
          </div>
          <div>
            <p className="text-gray-600">Active Time</p>
            <p className="font-semibold text-gray-900">{track.activeTimeSpent.toFixed(1)} mins</p>
          </div>
          <div>
            <p className="text-gray-600">Waste Time</p>
            <p className="font-semibold text-gray-900">{track.wasteTimeSpent.toFixed(1)} mins</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Grade section component
interface GradeSectionProps {
  grade: GradeAnalytics;
}

const GradeSection: React.FC<GradeSectionProps> = ({ grade }) => {
  const comparisonPercent = grade.gradeMetrics.benchmarkRate > 0
    ? ((grade.gradeMetrics.gradeLearningRate - grade.gradeMetrics.benchmarkRate) / grade.gradeMetrics.benchmarkRate * 100)
    : 0;

  const isFaster = comparisonPercent < 0;
  const comparisonText = isFaster
    ? `${Math.abs(comparisonPercent).toFixed(1)}% faster than benchmark`
    : `${comparisonPercent.toFixed(1)}% slower than benchmark`;

  return (
    <div className="mb-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">{grade.gradeName}</h2>

      {/* Tracks */}
      {grade.tracks.map((track) => (
        <TrackChart key={track.trackId} track={track} />
      ))}

      {/* Grade Summary */}
      <Card className="shadow-lg border border-gray-100 bg-blue-50">
        <CardContent className="p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">{grade.gradeName} Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Total XP</p>
              <p className="font-semibold text-gray-900">{grade.gradeMetrics.totalXpSpent.toFixed(1)} mins</p>
            </div>
            <div>
              <p className="text-gray-600">Learning Rate</p>
              <p className="font-semibold text-gray-900">
                {grade.gradeMetrics.gradeLearningRate.toFixed(3)} mins/fact/cqpm
              </p>
            </div>
            <div>
              <p className="text-gray-600">Benchmark</p>
              <p className="font-semibold text-gray-900">
                {grade.gradeMetrics.benchmarkRate.toFixed(3)} mins/fact/cqpm
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mt-4 pt-4 border-t border-gray-200">
            <div>
              <p className="text-gray-600">Total Time</p>
              <p className="font-semibold text-gray-900">{grade.gradeMetrics.totalTimeSpent.toFixed(1)} mins</p>
            </div>
            <div>
              <p className="text-gray-600">Active Time</p>
              <p className="font-semibold text-gray-900">{grade.gradeMetrics.totalActiveTimeSpent.toFixed(1)} mins</p>
            </div>
            <div>
              <p className="text-gray-600">Waste Time</p>
              <p className="font-semibold text-gray-900">{grade.gradeMetrics.totalWasteTimeSpent.toFixed(1)} mins</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-gray-600">Comparison</p>
            <p className={`font-semibold ${isFaster ? 'text-green-600' : 'text-orange-600'}`}>
              {comparisonText}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main dashboard component
export const StudentCqpmDashboard = () => {
  const studentNotLoggedInIndicator = 'AlphaNumbers profile has not been created yet';
  const { user: currentUser } = useAuth();
  const [data, setData] = useState<StudentCqpmAnalyticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchedEmail, setSearchedEmail] = useState<string | null>(null);
  const [searchedName, setSearchedName] = useState<string | null>(null);
  const [hasNotLoggedIn, setHasNotLoggedIn] = useState(false);

  const isAdmin = currentUser?.role === 'admin';

  const handleSearch = useCallback(async (email: string, name?: string) => {
    setIsLoading(true);
    setError(null);
    setData(null);
    setSearchedEmail(email);
    setSearchedName(name || null);
    setHasNotLoggedIn(false);

    try {
      const result = await getStudentCqpmAnalytics(email);
      setData(result);

      if (result.gradeData.length === 0) {
        setError('No assessment data found for this student.');
      }
    } catch (err) {
      if (err instanceof Error && err.message.includes(studentNotLoggedInIndicator)) {
        setHasNotLoggedIn(true);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch student analytics');
      }
    } finally {
      setIsLoading(false);
    }
  }, [studentNotLoggedInIndicator]);

  const handleClearSelection = useCallback(() => {
    setData(null);
    setError(null);
    setSearchedEmail(null);
    setSearchedName(null);
    setHasNotLoggedIn(false);
  }, []);

  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-6">
      <div className="max-w-7xl mx-auto">
        <DashboardHeader />

        <div className="flex mb-4">
          <Link
            to="/admin"
            className="text-blue-600 hover:text-blue-800 transition-colors duration-300"
          >
            ← Back to Admin Dashboard
          </Link>
        </div>

        <SearchWithAutocomplete
          onSelectStudent={(email, name) => handleSearch(email, name)}
          selectedStudentName={searchedName}
          selectedStudentEmail={searchedEmail}
          onClearSelection={handleClearSelection}
          isLoading={isLoading}
        />

        {/* Loading state */}
        {isLoading && (
          <Card className="shadow-lg border border-gray-100">
            <CardContent className="p-8 text-center">
              <div className="animate-pulse">
                <div className="h-8 bg-gray-300 rounded mb-4 mx-auto w-1/2"></div>
                <div className="h-4 bg-gray-300 rounded"></div>
              </div>
              <p className="text-gray-600 mt-4">Loading student analytics...</p>
            </CardContent>
          </Card>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <Card className="shadow-lg border border-red-200 bg-red-50">
            <CardContent className="p-6">
              <p className="text-red-600 font-semibold">Error</p>
              <p className="text-red-800">{error}</p>
            </CardContent>
          </Card>
        )}

        {hasNotLoggedIn && !isLoading && (
          <Card className="shadow-lg border border-amber-200 bg-amber-50">
            <CardContent className="p-8 text-center">
              <FiSearch className="w-10 h-10 mx-auto text-amber-500 mb-4" />
              <p className="text-amber-900 font-medium">AlphaNumbers account not set up yet</p>
              <p className="text-amber-800 text-sm mt-1">
                This student was found in roster search, but their AlphaNumbers profile has not been created yet. This usually happens before a student logs in for the first time, so there is no activity to show yet.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {data && !isLoading && data.gradeData.length > 0 && (
          <div>
            <Card className="shadow-lg border border-gray-100 mb-6 bg-white">
              <CardContent className="p-4">
                <p className="text-sm text-gray-600">Student</p>
                {searchedName && (
                  <p className="text-lg font-semibold text-gray-900">{searchedName}</p>
                )}
                <p className="text-lg font-semibold text-gray-900">{data.studentEmail}</p>
              </CardContent>
            </Card>

            {data.gradeData.map((grade) => (
              <GradeSection key={grade.grade} grade={grade} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentCqpmDashboard;
